<script setup lang="ts">
import { useColorPickerSwatchCrudHandlers } from "@/composition/useColorPickerSwatchCrudHandlers";
import { useColorPickerSwatches } from "@/composition/useColorPickerSwatches";
import { useColorPickerValueHandlers } from "@/composition/useColorPickerValueHandlers";
import { useColorResolver } from "@/composition/useColorResolver";
import { useAppStore } from "@/store/app";
import { toUpperHex, toUpperHexWithHash } from "@/util/color";
import { COLOR_PICKER_POPOVER_STYLE } from "@/util/colorPicker";
import { Settings01Icon } from "@gohighlevel/ghl-icons/24/outline";
import { UIInputNumber } from "@gohighlevel/ghl-ui";
import { HLButton, HLColorPicker, HLContentWrap } from "@platform-ui/highrise";
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
  border: {
    type: Number,
    default: 0,
  },
  borderColor: {
    type: String,
    default: "FFFFFF",
  },
  max: {
    type: Number,
    default: 100,
  },
});

const emit = defineEmits(["update:border", "update:color"]);

const store = useAppStore();
const { resolveHexFromProp } = useColorResolver(store);

const borderColor = ref(toUpperHexWithHash(resolveHexFromProp(props.borderColor)));

const handleBorder = (val: string) => emit("update:border", parseFloat(val) || 0);

const applyColorValue = (colorValue: string) => {
  borderColor.value = toUpperHexWithHash(colorValue);
  emit("update:color", toUpperHex(colorValue));
};

const { swatches, onSwatchAdd, handleDefaultSwatchAdd } = useColorPickerSwatches(store, {
  t,
  onApplyColor: applyColorValue,
});

const { handleSelect, handlePickerUpdate, handlePickerBack, handlePickerClose } =
  useColorPickerValueHandlers({
    getCommittedColor: () => resolveHexFromProp(props.borderColor),
    onColor: applyColorValue,
    onSelect: (hex) => store.recordBrandColorPickFromHex(hex),
  });

const { handleSwatchAdd, handleSwatchEdit, handleSwatchDelete } = useColorPickerSwatchCrudHandlers(
  store,
  {
    onCustomAdd: onSwatchAdd,
    onDefaultAdd: handleDefaultSwatchAdd,
    logPrefix: "BorderInput",
  }
);

watch(
  () => props.borderColor,
  (newColor) => (borderColor.value = toUpperHexWithHash(resolveHexFromProp(newColor)))
);
</script>

<template>
  <div class="grid grid-cols-2">
    <label for="border-input" class="my-auto text-sm font-normal text-gray-700">
      {{ t("common.border") }}
    </label>
    <div id="input-color-wrapper" class="input-color-picker">
      <UIInputNumber
        id="border-input"
        :show-button="false"
        :value="border"
        :min="0"
        :max="max"
        class="input-field"
        @change="handleBorder"
        @blur="store.addToHistory()"
      >
        <template #suffix>
          <span class="px">{{ t("common.px") }}</span>
        </template>
      </UIInputNumber>
      <div class="border-input-color">
        <HLContentWrap class="hl-color-picker-wrap">
          <HLColorPicker
            :value="borderColor"
            :swatches="swatches"
            :forward-picker-updates="true"
            :picker-options="{ showCTA: true }"
            :content-style="COLOR_PICKER_POPOVER_STYLE"
            @swatch:add="handleSwatchAdd"
            @swatch:edit="handleSwatchEdit"
            @swatch:delete="handleSwatchDelete"
            @update:picker="handlePickerUpdate"
            @update:picker:back="handlePickerBack"
            @update:picker:close="handlePickerClose"
            @select="handleSelect"
          >
            <template #swatchAddButton="{ sectionKey, sectionIndex }">
              <HLButton
                v-if="sectionKey === 'custom'"
                :id="`${sectionKey}-${sectionIndex}`"
                color="blue"
                text
                variant="secondary"
                class="p-0"
                @click="onSwatchAdd"
              >
                <Settings01Icon class="mr-1 h-3 w-3" />
                {{ $t("common.manage") }}
              </HLButton>
            </template>
          </HLColorPicker>
        </HLContentWrap>
      </div>
    </div>
  </div>
</template>

<style scoped>
.input-field {
  font-size: 12px;
}
.border-picker {
  position: relative;
}
.px {
  position: relative;
  left: -40px̦;
}
.input-color-picker {
  display: flex;
  width: 88%;
  align-items: center;
}
#input-color-wrapper,
#border-input {
  width: 88%;
}
.border-input-color {
  padding-left: 15px;
  display: flex;
  align-items: center;
}
</style>
